import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { showToast } from '../context/ToastContext';

export const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      showToast.error('Please fill in both fields.');
      return;
    }

    setSubmitting(true);

    const loggedInUser = await login(username, password);
    if (loggedInUser) {
      navigate(`/${loggedInUser.role}`, { replace: true });
    } else {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0d0f14] p-4 font-sans">
      {/* Background glows (decorative, match dark theme) */}
      <div className="absolute top-1/4 left-1/4 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#3b82f6]/5 blur-[100px]" />
      <div className="absolute bottom-1/4 right-1/4 h-[450px] w-[450px] translate-x-1/2 translate-y-1/2 rounded-full bg-[#22c55e]/5 blur-[120px]" />

      <div className="relative w-full max-w-md overflow-hidden rounded-xl border border-[#252a38] bg-[#161920] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.5)]">
        {/* Header */}
        <div className="flex flex-col items-center px-6 pt-8 pb-2">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[#2d4a8a] bg-[#1e2a4a]">
            <LogIn size={24} className="text-[#3b82f6]" />
          </div>
          <h1 className="mt-5 text-[20px] font-bold text-[#e2e8f0]">
            Welcome Back
          </h1>
          <p className="mt-1.5 text-[13px] text-[#64748b]">
            Sign in to access your dashboard
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 px-6 pt-6 pb-6">
          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64748b]">
              Username
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={submitting}
              className="w-full rounded-lg border border-[#252a38] bg-[#0d0f14] px-3.5 py-2.5 text-[13px] font-semibold text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6] disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Enter username"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.08em] text-[#64748b]">
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
              className="w-full rounded-lg border border-[#252a38] bg-[#0d0f14] px-3.5 py-2.5 text-[13px] font-semibold text-[#e2e8f0] outline-none transition-colors placeholder:text-[#475569] focus:border-[#3b82f6] disabled:cursor-not-allowed disabled:opacity-50"
              placeholder="Enter password"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="flex w-full items-center justify-center gap-2 rounded-lg border-none bg-[#3b82f6] px-4 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-[#2563eb] focus:outline-none disabled:cursor-not-allowed disabled:bg-[#1e2235] disabled:text-[#334155]"
          >
            {submitting ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Signing In...
              </>
            ) : (
              <>
                <LogIn size={16} />
                Sign In
              </>
            )}
          </button>
        </form>

      </div>
    </div>
  );
};

export default Login;